import React, { useState } from "react";
import { Collapse, Select, Input, Button } from "antd";
import HtmlEditor from "./HtmlEditor";
import ItineraryMapping from "./ItineraryMapping";
import FileLibraryModal from "./FileLibraryModal";
import { TYPE_LABEL, SUB_LABEL, SYSTEM_LABEL, isEmpty, mustFill } from "../logic";

const cut = (t) => (t.length > 140 ? `${t.slice(0, 140)}…` : t);

// A value that won't work where the template puts it (the admin can still choose it).
const typeWarning = (info, v) => {
  if (!v.type) return null;
  const label = { text: "Text", html: "HTML", system: "A system variable" }[v.type];
  if (info.in_href && v.type !== "url")
    return `This variable is a link (href) in the template. ${label} here won't make a working link — use URL / Link.`;
  if (info.in_src && v.type !== "url")
    return `This variable is an image address (src) in the template. ${label} here won't show an image — use URL / Link.`;
  return null;
};

const RULE_TAG = {
  default: <span className="tag">optional · has default</span>,
  hides_block: <span className="tag">optional · hides block</span>,
  required: <span className="tag warn">required</span>,
};

// One line shown while the variable is collapsed
const briefText = (v, info) => {
  if (!v.type) {
    if (info.default == null) return "No type chosen";
    const shown = info.default.replace(/<[^>]+>/g, "").trim();
    return `Template default · ${shown ? cut(shown) : "empty"}`;
  }
  if (v.type === "url") return `${TYPE_LABEL.url} · ${SUB_LABEL[v.url_sub_type] || ""}${v.url_sub_type === "custom" && v.value ? ` · ${cut(v.value)}` : ""}`;
  if (v.type === "system") return `${TYPE_LABEL.system} · ${SYSTEM_LABEL[v.system_variable] || ""}`;
  const plain = (v.value || "").replace(/<[^>]+>/g, "").trim();
  return plain ? `${TYPE_LABEL[v.type]} · ${cut(plain)}` : `${TYPE_LABEL[v.type]} · empty`;
};

/** One template variable: how it's used in the template, and how the admin fills it. */
const VariableRow = ({ info, v: value, varsByKey, onChange, recipientIds }) => {
  // only one possible type (e.g. preheader-only = Text): always show that input
  const v = info.types.length === 1 && value.type !== info.types[0] ? { ...value, type: info.types[0] } : value;
  const [pickFile, setPickFile] = useState(false);
  // open while the variable is empty; closed once a value is set or the template default is used
  const usesDefault = !v.type && info.default != null;
  const [open, setOpen] = useState(() => isEmpty(value) && !usesDefault);
  const srcOnly = info.in_src && !info.in_href; // an image source: only a custom URL or an image file
  const subs = info.url_sub_types.map((k) => [k, SUB_LABEL[k]]);
  const needs = mustFill(info, varsByKey);
  const bad = needs && isEmpty(v);
  const set = (patch) => onChange({ ...v, ...patch });
  const warning = typeWarning(info, v);

  const typeSelect = (
    <Select aria-label={`Type for ${info.key}`} value={v.type || ""} onChange={(type) => set({ type, value: "", file: null, url_sub_type: info.url_sub_types.includes(v.url_sub_type) ? v.url_sub_type : info.url_sub_types[0] })}
      options={[{ value: "", label: info.optional ? (info.default != null ? "Use template default" : "Leave empty") : "Choose type…" },
        ...info.types.map((value) => ({ value, label: TYPE_LABEL[value] }))]} />
  );

  let second = <span />;
  let third = null;
  let below = null;
  if (v.type === "url") {
    second = (
      <Select aria-label={`Link type for ${info.key}`} value={v.url_sub_type} options={subs.map(([value, label]) => ({ value, label }))}
        onChange={(url_sub_type) => set({ url_sub_type, value: "", file: null, mappings: url_sub_type === "itinerary_download" && !v.mappings.length ? [{ users: [], itinerary: null }] : v.mappings })} />
    );
    if (v.url_sub_type === "custom") {
      const invalid = v.value && !/^https?:\/\//i.test(v.value.trim());
      third = (
        <div className="full">
          <Input aria-label={`URL for ${info.key}`} status={invalid ? "error" : undefined} value={v.value} placeholder="https://…" onChange={(e) => set({ value: e.target.value })} />
          {invalid && <div className="rerr" style={{ fontSize: 12 }}>The URL must start with http:// or https://</div>}
        </div>
      );
    } else if (v.url_sub_type === "file") {
      third = (
        <div className="full row">
          {v.file && <span className="tag" style={{ whiteSpace: "normal" }}>{v.file.original_name}</span>}
          <Button size="small" onClick={() => setPickFile(true)}>{v.file ? "Change file" : "Upload or choose file"}</Button>
          <FileLibraryModal open={pickFile} onClose={() => setPickFile(false)} onPick={(file) => { setPickFile(false); set({ file }); }} accept={srcOnly ? "image" : "any"} />
        </div>
      );
    } else if (v.url_sub_type === "itinerary_download") {
      third = <div className="full resolved">PDF of the itinerary picked for each user below</div>;
      below = <ItineraryMapping rows={v.mappings} onChange={(mappings) => set({ mappings })} recipientIds={recipientIds} />;
    } else if (v.url_sub_type === "deeplink") {
      third = <div className="full resolved">One tracked link per user · opens the Travezy app, or the store page if it isn't installed</div>;
    } else {
      third = <div className="full resolved">Tracked feedback link · counts {v.url_sub_type === "useful" ? "Useful" : "Not Useful"} and shows a thank-you page</div>;
    }
  } else if (v.type === "system") {
    second = <Select aria-label={`System variable for ${info.key}`} value={v.system_variable} onChange={(system_variable) => set({ system_variable })}
      options={Object.entries(SYSTEM_LABEL).map(([value, label]) => ({ value, label }))} />;
    third = <div className="full resolved">From <span className="mono">users.{v.system_variable}</span> for each recipient</div>;
  } else if (v.type === "text") {
    second = <span className="note">Plain text</span>;
    third = (
      <div className="full">
        <Input aria-label={`Text for ${info.key}`} value={v.value} onChange={(e) => set({ value: e.target.value })}
          placeholder={info.default != null ? "Empty = template default · {firstname} {lastname} {itinerary_1} allowed" : "Text · {firstname} {lastname} {itinerary_1} allowed"} />
      </div>
    );
  } else if (v.type === "html") {
    second = <span className="note">Rich content</span>;
    below = (
      <>
        <HtmlEditor ariaLabel={`HTML content for ${info.key}`} value={v.value} onChange={(value) => set({ value })} />
        <div className="note">
          <b>{"{firstname}"}</b> / <b>{"{lastname}"}</b> stay as they are here and become each recipient's name in the preview and the email.
          Fonts other than common ones (Arial, Georgia, Courier New…) show as the reader's default font in most email apps.
        </div>
      </>
    );
  } else {
    const why = { default: "OneSignal uses the template default", hides_block: "OneSignal hides its {% if %} block",
      conditional: `fine while ${info.required_when.join(", ")} is empty` }[info.rule];
    third = (
      <div className="full note">
        {needs ? "Required. The template has no default for it." : `Left out of custom_data: ${why}.`}
        {info.in_href && !needs && info.defaults.length > 0 && (
          <div className="rerr" style={{ marginTop: 4 }}>
            ⚠ The template's own default link is used, so clicks on it are not counted. Choose URL / Link (e.g. Useful / Not Useful) to track them.
          </div>
        )}
      </div>
    );
  }

  const header = (
    <div>
      <div className="varhead">
        <span className="vkey">{`{{ message.custom_data.${info.key} }}`}</span>
        <span className="ctx">
          {v.remembered && <span className="tag new" title="Type used the last time this template was sent">Saved type</span>}
          {info.rule === "conditional" ? <span className="tag warn">required if {info.required_when.join(", ")} is set</span> : RULE_TAG[info.rule]}
          {bad && <span className="tag bad">needs value</span>}
        </span>
      </div>
      {!open && <div className="note">{briefText(v, info)}</div>}
    </div>
  );

  const body = (
    <>
      <div className="ctx">
        {/* where the template prints it, and how many times */}
        {Object.entries(info.places).filter(([, n]) => n).map(([place, n]) => (
          <span className="tag" key={place}>{place}{n > 1 ? ` ×${n}` : ""}</span>
        ))}
        {info.in_href && <span className="tag">link · tracked</span>}
        {info.in_src && <span className="tag warn">image src · not tracked</span>}
        {info.in_if && <span className="tag">{"{% if %} block"}</span>}
      </div>
      {info.defaults.length > 0 && (
        <div className="dflt">
          Template default{info.defaults.length > 1 ? "s" : ""}: {info.defaults.map((d, i) => <code key={i} style={{ marginRight: 4 }}>{cut(d)}</code>)}
        </div>
      )}
      <div className="varctl">
        {/* only in the preheader: always plain text, so just the text box */}
        {info.types.length > 1 && typeSelect}
        {info.types.length > 1 && second}
        {third}
      </div>
      {warning && <div className="warnbox">⚠ {warning}</div>}
      {below}
    </>
  );

  return (
    <div className={`var ${bad ? "missing" : ""}`}>
      <Collapse ghost expandIconPosition="end" activeKey={open ? ["v"] : []} onChange={(keys) => setOpen(keys.length > 0)}
        items={[{ key: "v", label: header, children: body, forceRender: true }]} />
    </div>
  );
};

export default VariableRow;
